import type { DeviceDefinition, Extension } from "@medplum/fhirtypes";
import { AVAILABILITIES, type Availability } from "@repo/domain";
import { extensionUrl } from "@repo/fhir-systems";
import { DEVICE_DEFINITION_LINEAGE_SYSTEM, DEVICE_DEFINITION_REVISION_SYSTEM } from "@repo/fhir-systems/identifiers";

/**
 * Where a `DeviceDefinition` row keeps the catalog facts the views read
 * (ADR-0021: views read FHIR rows directly and never import codecs).
 *
 * R4's `DeviceDefinition` has no `status` and no `date`, and spends `url` and
 * `version` on the product rather than the lineage — so the lineage url and
 * revision are identifiers, availability and the minting time are extensions,
 * and the name is the `model-name` device name.
 *
 * A row missing any of them THROWS rather than reading as `""`, `NaN` or
 * `Inactive`. It is the codec's `required`/`oneOf` disposition on the read
 * side: a substituted value is indistinguishable from a real one, so every
 * broken row would silently share a `""` lineage, and an availability code we
 * do not know would hide an active kind from the active list. `deriveForSync`
 * in `./base.ts` catches this, logs the resource id and drops that one row, so
 * loud here costs one row rather than the collection.
 */

export const DEVICE_MINTED_AT_URL = extensionUrl("catalog-minted-at");
export const DEVICE_AVAILABILITY_URL = extensionUrl("catalog-availability");
export const DEVICE_MEMBER_MARKS_URL = extensionUrl("catalog-member-marks");
export const DEVICE_PARAMETER_URL = extensionUrl("catalog-parameter");

export { DEVICE_DEFINITION_LINEAGE_SYSTEM, DEVICE_DEFINITION_REVISION_SYSTEM };

export type DeviceDefinitionAvailability = Availability;

function unreadable(resource: DeviceDefinition, field: string, value: unknown): never {
  throw new Error(
    `DeviceDefinition row ${resource.id ?? "<unknown>"} has no readable ${field}${
      value === undefined ? "" : ` (${JSON.stringify(value)})`
    }`,
  );
}

export function extension(extensions: Extension[] | undefined, url: string): Extension | undefined {
  return extensions?.find((candidate) => candidate.url === url);
}

function identifierValue(resource: DeviceDefinition, system: string): string | undefined {
  return resource.identifier?.find((identifier) => identifier.system === system)?.value;
}

/** The lineage canonical, shared by every revision. */
export function deviceDefinitionUrl(resource: DeviceDefinition): string {
  return identifierValue(resource, DEVICE_DEFINITION_LINEAGE_SYSTEM) ?? unreadable(resource, "lineage url", undefined);
}

export function deviceDefinitionVersion(resource: DeviceDefinition): number {
  const stored = identifierValue(resource, DEVICE_DEFINITION_REVISION_SYSTEM);
  const version = Number(stored);
  return Number.isFinite(version) ? version : unreadable(resource, "revision", stored);
}

export function deviceDefinitionName(resource: DeviceDefinition): string {
  return (
    resource.deviceName?.find((name) => name.type === "model-name")?.name ?? unreadable(resource, "name", undefined)
  );
}

export function deviceDefinitionAvailability(resource: DeviceDefinition): DeviceDefinitionAvailability {
  const stored = extension(resource.extension, DEVICE_AVAILABILITY_URL)?.valueCode;
  return AVAILABILITIES.includes(stored as Availability)
    ? (stored as Availability)
    : unreadable(resource, "availability", stored);
}

export function deviceDefinitionMintedAt(resource: DeviceDefinition): string {
  return (
    extension(resource.extension, DEVICE_MINTED_AT_URL)?.valueDateTime ?? unreadable(resource, "mintedAt", undefined)
  );
}

/** The description rides on `note`, which is what a free-text comment on a device is for. */
export function deviceDefinitionDescription(resource: DeviceDefinition): string | undefined {
  return resource.note?.[0]?.text;
}
